import { Component, ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
  fallbackMessage?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  autoRetryCount: number;
  isAutoRetrying: boolean;
}

// Transient errors that can be auto-retried (e.g. React concurrent mode race conditions)
const AUTO_RETRYABLE_PATTERNS = [
  'fewer hooks than expected',
  'more hooks than expected',
  'Rendered more hooks',
  'Rendered fewer hooks',
  // Minified React production errors (same bugs, but message is minified)
  'Minified React error #300', // Rendered more hooks than previous render
  'Minified React error #301', // Rendered fewer hooks than previous render
];

// Check if an error message matches auto-retryable patterns
function isAutoRetryableError(message?: string): boolean {
  if (!message) return false;
  return AUTO_RETRYABLE_PATTERNS.some(p => message.includes(p));
}

const MAX_AUTO_RETRIES = 2;

class ErrorBoundary extends Component<Props, State> {
  private autoRetryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, autoRetryCount: 0, isAutoRetrying: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // For auto-retryable errors, don't show the error UI - just mark for retry
    if (isAutoRetryableError(error.message)) {
      return { hasError: true, error, isAutoRetrying: true };
    }
    return { hasError: true, error, isAutoRetrying: false };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("[ErrorBoundary] Caught error:", error, errorInfo);

    // Auto-reload for stale chunk errors (after new deployments)
    const isChunkError = error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('Importing a module script failed') ||
      error.message?.includes('error loading dynamically imported module');

    if (isChunkError) {
      try {
        const RELOAD_TS_KEY = '__chunk_reload_ts';
        const lastReload = Number(sessionStorage.getItem(RELOAD_TS_KEY) || '0');
        if (Date.now() - lastReload < 10000) {
          console.warn('[ErrorBoundary] Chunk reload already attempted recently, skipping.');
          return;
        }
        sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now()));
      } catch { /* sessionStorage unavailable */ }
      console.log('[ErrorBoundary] Stale chunk detected, reloading page...');
      window.location.reload();
      return;
    }

    // Auto-retry for transient hook-ordering errors (session swap race conditions)
    if (isAutoRetryableError(error.message) && this.state.autoRetryCount < MAX_AUTO_RETRIES) {
      console.log(`[ErrorBoundary] Auto-retrying transient error (attempt ${this.state.autoRetryCount + 1}/${MAX_AUTO_RETRIES})`);
      // Use microtask for near-instant retry without showing error UI
      this.autoRetryTimer = setTimeout(() => {
        this.setState(prev => ({
          hasError: false,
          error: null,
          autoRetryCount: prev.autoRetryCount + 1,
          isAutoRetrying: false,
        }));
      }, 0);
    }
  }

  componentWillUnmount() {
    if (this.autoRetryTimer) clearTimeout(this.autoRetryTimer);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, autoRetryCount: 0 });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    // During auto-retry, render nothing instead of the error UI to prevent flash
    if (this.state.hasError && this.state.isAutoRetrying) {
      return null;
    }

    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="mx-auto w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>
            <div className="space-y-2">
              <h1 className="text-xl font-semibold text-foreground">
                {this.props.fallbackMessage || "Etwas ist schiefgelaufen"}
              </h1>
              <p className="text-sm text-muted-foreground">
                Die Seite konnte nicht geladen werden. Bitte versuche es erneut.
              </p>
              {this.state.error && (
                <details className="mt-3 text-left">
                  <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                    Technische Details
                  </summary>
                  <pre className="mt-2 p-3 bg-muted rounded-lg text-xs text-destructive overflow-auto max-h-40 whitespace-pre-wrap break-words">
                    {this.state.error.message}
                    {this.state.error.stack && `\n\n${this.state.error.stack}`}
                  </pre>
                </details>
              )}
            </div>
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition text-sm font-medium"
              >
                <RefreshCw className="h-4 w-4" />
                Erneut versuchen
              </button>
              <button
                onClick={this.handleReload}
                className="inline-flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:opacity-90 transition text-sm font-medium"
              >
                Seite neu laden
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
